import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Button } from '@bigcommerce/big-design';
import { CloseIcon } from '@bigcommerce/big-design-icons';

const Overlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.6);
	z-index: 999;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Modal = styled.div`
	position: relative;
	background: #fff;
	border-radius: 8px;
	max-width: 90vw;
	max-height: 90vh;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled.div`
	position: absolute;
	top: 8px;
	right: 8px;
	z-index: 2;
	cursor: pointer;
	background: rgba(255, 255, 255, 0.9);
	border-radius: 50%;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const MediaContainer = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: auto;
	flex: 1;
	min-height: 0;
`;

const ModalImage = styled.img`
	display: block;
	max-width: 90vw;
	max-height: calc(90vh - 250px);
	object-fit: contain;
`;

const ModalVideo = styled.video`
	display: block;
	max-width: 90vw;
	max-height: calc(90vh - 250px);
`;

const ModalIframe = styled.iframe`
	display: block;
	width: min(80vw, 800px);
	height: min(45vw, 450px);
	border: none;
`;

const Dots = styled.div`
	display: flex;
	justify-content: center;
	gap: 8px;
	padding: 8px 0;
`;

const Dot = styled.button`
	width: 10px;
	height: 10px;
	border-radius: 50%;
	border: none;
	cursor: pointer;
	background: ${(props) => (props.$active ? '#3c64f4' : '#d1d5db')};
	transition: background 0.2s;

	&:hover {
		background: ${(props) => (props.$active ? '#3c64f4' : '#9ca3af')};
	}
`;

const InfoBar = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px 24px;
	padding: 16px 24px;
	background: #f9fafb;
	border-top: 1px solid #e5e7eb;
	font-size: 14px;
	color: #374151;
`;

const InfoItem = styled.span`
	white-space: nowrap;

	strong {
		color: #111827;
	}
`;

const StoryText = styled.div`
	padding: 8px 24px 0;
	background: #f9fafb;
	font-size: 14px;
	color: #555;
	font-style: italic;
`;

const Footer = styled.div`
	display: flex;
	justify-content: center;
	gap: 40px;
	padding: 40px 48px;
	border-top: 1px solid #e5e7eb;
	background: #fff;

	button {
		padding: 24px 48px;
		font-size: 20px;
	}
`;

const NavButton = styled.button`
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	z-index: 2;
	background: rgba(255, 255, 255, 0.9);
	border: none;
	border-radius: 50%;
	width: 100px;
	height: 100px;
	font-size: 48px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);

	&:hover {
		background: #fff;
	}

	${(props) => (props.$left ? 'left: 8px;' : 'right: 8px;')}
`;

export default function PhotoContestPanel({
	submission,
	onClose,
	onUpdateStatus,
}) {
	const [mediaIndex, setMediaIndex] = useState(0);

	// Reset index when a different submission opens
	useEffect(() => {
		setMediaIndex(0);
	}, [submission?.id]);

	const mediaItems = [];
	if (submission) {
		(submission.photoUrls || []).forEach((url) =>
			mediaItems.push({ type: 'photo', url }),
		);
		if (submission.videoUrl) {
			mediaItems.push({ type: 'video', url: submission.videoUrl });
		}
	}

	const current = mediaItems[mediaIndex] || mediaItems[0];
	const hasPrev = mediaIndex > 0;
	const hasNext = mediaIndex < mediaItems.length - 1;

	const handlePrev = useCallback(
		(e) => {
			if (e) e.stopPropagation();
			if (mediaIndex > 0) setMediaIndex(mediaIndex - 1);
		},
		[mediaIndex],
	);

	const handleNext = useCallback(
		(e) => {
			if (e) e.stopPropagation();
			if (mediaIndex < mediaItems.length - 1)
				setMediaIndex(mediaIndex + 1);
		},
		[mediaIndex, mediaItems.length],
	);

	// Keyboard navigation
	useEffect(() => {
		if (!submission) return;
		const handleKey = (e) => {
			if (e.key === 'ArrowLeft') handlePrev();
			else if (e.key === 'ArrowRight') handleNext();
			else if (e.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', handleKey);
		return () => window.removeEventListener('keydown', handleKey);
	}, [submission, handlePrev, handleNext, onClose]);

	if (!submission) return null;

	const handleOverlayClick = (e) => {
		if (e.target === e.currentTarget) onClose();
	};

	return (
		<Overlay onClick={handleOverlayClick}>
			<Modal>
				<CloseButton onClick={onClose}>
					<CloseIcon />
				</CloseButton>

				<MediaContainer>
					{hasPrev && (
						<NavButton $left onClick={handlePrev}>
							‹
						</NavButton>
					)}
					{hasNext && (
						<NavButton onClick={handleNext}>›</NavButton>
					)}
					{current?.type === 'video' && current.url.includes('youtube.com') ? (
						<ModalIframe
							key={current.url}
							src={`${current.url}?autoplay=1&mute=1`}
							allow="autoplay; encrypted-media"
							allowFullScreen
						/>
					) : current?.type === 'video' ? (
						<ModalVideo
							key={current.url}
							controls
							autoPlay
							src={current.url}
						/>
					) : current?.url ? (
						<ModalImage
							key={current.url}
							src={current.url}
							alt={submission.name}
						/>
					) : null}
				</MediaContainer>

				{mediaItems.length > 1 && (
					<Dots>
						{mediaItems.map((item, i) => (
							<Dot
								key={i}
								$active={i === mediaIndex}
								onClick={() => setMediaIndex(i)}
							/>
						))}
					</Dots>
				)}

				<InfoBar>
					<InfoItem>
						<strong>{submission.name}</strong>
					</InfoItem>
					{submission.email && (
						<InfoItem>{submission.email}</InfoItem>
					)}
					<InfoItem>Order #{submission.orderId}</InfoItem>
					{(() => {
						const loc = [
							submission.city,
							submission.state,
							submission.country,
						]
							.filter(Boolean)
							.join(', ');
						return loc ? <InfoItem>{loc}</InfoItem> : null;
					})()}
					{submission.productName && (
						<InfoItem>{submission.productName}</InfoItem>
					)}
					{submission.instagramHandle && (
						<InfoItem>{submission.instagramHandle}</InfoItem>
					)}
					<InfoItem>{new Date(submission.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</InfoItem>
				</InfoBar>
				{submission.story && (
					<StoryText>"{submission.story}"</StoryText>
				)}

				<Footer>
					<Button
						variant="primary"
						onClick={() =>
							onUpdateStatus(submission.id, 'approved')
						}
						disabled={submission.status === 'approved'}
					>
						Approve
					</Button>
					<Button
						variant="secondary"
						onClick={() =>
							onUpdateStatus(submission.id, 'rejected')
						}
						disabled={submission.status === 'rejected'}
						style={{
							backgroundColor: submission.status === 'rejected' ? undefined : '#db3643',
							color: submission.status === 'rejected' ? undefined : '#fff',
							borderColor: submission.status === 'rejected' ? undefined : '#db3643',
						}}
					>
						Reject
					</Button>
				</Footer>
			</Modal>
		</Overlay>
	);
}
